








































































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { ContentWidth, HeroBannerViewModel, HorizontalAlignment, SubheaderFontSize, Media } from '@/types/contentServerContract';
import CallToAction from '@/project/form/CallToAction.vue';
import VimeoPlayer from '@vimeo/player';
import breakpointState from '@/core/responsive/breakpoints/breakpointsState.observable';
import { MAX_MD, MIN_LG, MIN_MD, MAX_SM, MIN_XS } from '@/core/config/breakpointConfig';
import { hexToRgb, isValidHexColor } from '@/core/color/color.service';
import { AdContentBlockViewModel, AdImage } from '@/types/apiServerContract';
import SpinnerOverlay from '@/project/spinners/SpinnerOverlay.vue';
import SmartHeadline from '@/core/ui-components/SmartHeadline.vue';
import tailwind from '@/styling/tailwind.config';
import themeService from '@/project/app/themes/theme.service';
import loggingService from '@/core/logging.service';
import SmartParagraph from '@/core/ui-components/SmartParagraph.vue';
import videoFullscreenOverlayService from '@/project/modals/overlays/VimeoVideoFullscreenOverlay.service';
import responsiveImageService from '@/core/responsive/image/responsiveImage.service';
import serverContextService from '@/core/serverContext.service';

const mobileAspectRatio = 3 / 2;
const semiTransparencyPercentage = 0.75;
const videoAspectRatio = 16 / 9;
const wideBannerAspectRatio = 5;

interface VideoElm {
    play: () => void;
    pause: () => void;
    on: any;
    onloadeddata: () => void;
    getVideoWidth: () => Promise<number>;
    getVideoHeight: () => Promise<number>;
}
interface CombinedHeroBannerViewModel extends HeroBannerViewModel {
    // Mixing HeroBannerViewModel with stuff from AdContentBlockViewModel
    overlayVimeoVideoId?:string;
    overlayVimeoCta?:string;
}

@Component({
    components: { SmartParagraph, SpinnerOverlay, CallToAction, SmartHeadline }
})
export default class ImageTextVideoBlockNew extends Vue {
    /* Could also be a BaseBlockViewModel - but we handle the missing props:
       contentAlignment, contentWidth, subheaderFontSize.
       Could also be a AdContentBlockViewModel, but we handle the extra backgroundImages special */
    @Prop({
        type: Object
    }) content!: CombinedHeroBannerViewModel | null;

    @Prop({
        type: Boolean,
        default: false
    }) isFirstBlock!: boolean;

    @Prop({
        type: Boolean,
        default: false
    }) contentLoadError!: boolean;

    @Prop({
        type: Function,
        default: () => {}
    }) clickHandler!: () => void;

    @Prop({
        type: Boolean,
        default: false
    }) isHeroBanner!: boolean;

    @Prop({
        type: Boolean,
        default: false
    }) useFlexBox!: boolean;

    @Prop({
        required: true,
        type: Number
    }) widthOnScreen!: number;

    @Prop({
        required: true,
        type: Number
    }) aspectRatio!: number;

    @Prop()
    forceVimeoAspectRatio!: number;

    @Prop({
        required: false,
        type: Object
    }) aspectRatioMargins!: { [bp: string]: number };

    get shouldUseFlexBox() {
        return this.useFlexBox && !this.isMobile;
    }

    video: VimeoPlayer | VideoElm | null = null;
    isPlaying: boolean = false;
    loadingVideo: boolean = true;
    contentBackgroundColor: string = '';
    vimeoVideoAspectRatio: { width: number, height: number } | null = null;
    backgroundImageWidth: number | null = null;
    rendered: boolean = false;

    get isWithinWideBannerAspectRatio() {
        return this.aspectRatio >= wideBannerAspectRatio;
    }

    get isMobile() {
        return breakpointState.isBreakpointActive(MAX_SM);
    }

    get hasTextContent(): boolean {
        // By purpose: dont look at callToAction
        return !!(this.content?.header || this.content?.subheader || this.content?.caption);
    }

    get elementClasses(): string[] {
        const classes: string[] = [];
        if (this.content?.vimeoVideoId || this.content?.video || this.showBackgroundImage) {
            classes.push('has-video-or-image');
        }
        if (this.shouldUseFlexBox && this.hasTextContent) {
            classes.push('flex flex-col flex-1');
        }
        if (this.isHeroBanner && this.hasTextContent) {
            classes.push('flex');
        }
        if ((this.shouldUseFlexBox && !this.hasTextContent) || (this.hasTextContent && !this.showBackgroundImage)) {
            classes.push('h-full');
        }
        if (this.showBackgroundImage && (!this.isHeroBanner && !this.isBackgroundImageBehaviorImageDimensions)) {
            classes.push('min-h-280 md:min-h-350 lg:min-h-400');
        }
        if (this.showBackgroundImage) {
            classes.push('md:h-full');
        }
        return classes;
    }

    get isBackgroundImageBehaviorCover(): boolean {
        return this.content?.backgroundImageBehavior === 'cover';
    }

    get isBackgroundImageBehaviorImageDimensions(): boolean {
        return this.content?.backgroundImageBehavior === 'imageDimensions';
    }

    get styleForParentContainer(): Partial<CSSStyleDeclaration> {
        const styling: Partial<CSSStyleDeclaration> = {};
        if (this.video) {
            styling.backgroundColor = this.backgroundColorStyle.backgroundColor;
            const elWidth = this.$el?.clientWidth || 0;
            const minHeight = elWidth / ((this.vimeoVideoAspectRatio && this.vimeoVideoAspectRatio.width / this.vimeoVideoAspectRatio.height) || 16 / 9);
            styling.minHeight = `${minHeight}px`;
        }
        return styling;
    }

    get styleForImageBackground(): Partial<CSSStyleDeclaration> | null {
        const styling: Partial<CSSStyleDeclaration> = {};
        if (this.content?.backgroundImage) {
            const backgroundImageBehavior: string = this.content.backgroundImageBehavior;
            let backgroundSize: string = 'cover';
            let aspectRatio: null | number = this.isMobile ? this.content.backgroundImage.width / this.content.backgroundImage.height : null;

            if (this.isBackgroundImageBehaviorImageDimensions) {
                // Set min-height if HalfWidthBlock has very little text next to an image
                const elWidth = this.$el?.clientWidth || 0;
                const imageAspectRatio = this.content.backgroundImage.width / this.content.backgroundImage.height;
                const minHeight = elWidth / imageAspectRatio;
                styling.minHeight = `${minHeight}px`;
                backgroundSize = 'contain';
            } else if (backgroundImageBehavior === 'cover') {
                aspectRatio = null;
            } else if (backgroundImageBehavior === 'alignTop') {
                this.content.backgroundImage.focalPoint.top = 0;
                backgroundSize = '100% auto';
            } else if (backgroundImageBehavior === 'center') {
                backgroundSize = '100% auto';
            }

            let newBackgroundWidth = this.content.backgroundImage.width;
            let newBackgroundHeight = this.content.backgroundImage.height;
            // Upscale image from element width, because we use background-size: cover that "might" (most likely will) scale the image a bit.
            // Using a low number (e.g. 1.2) makes images look bad in most cases
            if (this.backgroundImageWidth !== null) {
                const upsizeFactor = 1.5;
                const doResize = this.content.backgroundImage.width > this.backgroundImageWidth;
                const allowUpscale: boolean = backgroundImageBehavior === 'cover' && this.content.backgroundImage.width > this.backgroundImageWidth * upsizeFactor;

                if (allowUpscale && doResize) {
                    newBackgroundWidth = Math.round(this.backgroundImageWidth * upsizeFactor);
                    newBackgroundHeight = Math.round(this.content.backgroundImage.height / this.content.backgroundImage.width * this.backgroundImageWidth * upsizeFactor);
                }
            }

            const backgroundImageUrl = this.formatImageUrl(this.content.backgroundImage, newBackgroundWidth, newBackgroundHeight, 80);
            styling.flex = '1';
            styling.backgroundSize = backgroundSize;
            styling.backgroundPosition = `${100 * this.content.backgroundImage.focalPoint.left}% ${100 * this.content.backgroundImage.focalPoint.top}%`;
            styling.backgroundRepeat = 'no-repeat';
            // @ts-ignore
            styling.aspectRatio = aspectRatio;
            if (this.rendered) {
                styling.backgroundImage = `url(${backgroundImageUrl})`;
            }
        }
        styling.backgroundColor = this.backgroundColorStyle.backgroundColor;
        if (this.content?.vimeoVideoId && this.vimeoVideoAspectRatio !== null) {
            styling.height = '100%';
        } else if (this.isHeroBanner && this.content) {
            if (!this.hasTextContent && this.content.backgroundImage) {
                // Hero banner without text
                // @ts-ignore
                styling.aspectRatio = this.content.backgroundImage.width / this.content.backgroundImage.height;
            } else if (this.content.subheader.length < 80) {
                // Force old aspect ratio to Hero Banner if there is only a limited amount of text
                const paddingPct = (1 / this.realAspectRatio) * 100;
                // @ts-ignore
                styling.aspectRatio = `calc(${paddingPct}% - ${this.margin}px)`;
            }
        }
        return styling;
    }

    formatImageUrl(image: Media, width: number, height: number, quality?: number): string {
        const imageUrl = image.url;
        const focalPoint = image.focalPoint;
        const firstSeparator = !imageUrl || imageUrl.indexOf('?') === -1 ? '?' : '&';
        const focal = focalPoint ? `&center=${focalPoint.top},${focalPoint.left}` : '';
        const qualityFormatted = quality ? `&quality=${quality}` : '';
        const format = image.extension ? `&format=${image.extension}` : '';
        return `${this.absoluteImageUrl(imageUrl)}${firstSeparator}width=${width}&height=${height}${format}${focal}${qualityFormatted}`;
    }

    absoluteImageUrl(imageUrl: string): string {
        return responsiveImageService.getBaseUrl() + imageUrl;
    }

    get realAspectRatio(): number {
        if (this.shouldUseFlexBox) return 0;
        if (this.content?.backgroundImage && this.content.backgroundImageBehavior !== 'cover') {
            return this.content.backgroundImage.width / this.content.backgroundImage.height;
        }
        if (!this.isMobile && this.forceVimeoAspectRatio) return this.forceVimeoAspectRatio;

        // For mobile, aspect-ratio is controlled from within.
        return this.isMobile ? mobileAspectRatio : this.vimeoVideoAspectRatio ? (this.vimeoVideoAspectRatio.width / this.vimeoVideoAspectRatio.height) : this.aspectRatio;
    }

    get realWidthOnScreen(): number {
        // For mobile, width is controlled from within.
        return this.isMobile ? 100 : this.widthOnScreen;
    }

    get margin() {
        if (!this.aspectRatioMargins) return 0;

        const activeBpDef = Object.keys(this.aspectRatioMargins).find(bpDef => breakpointState.isBreakpointActive(bpDef));
        const result = activeBpDef ? this.aspectRatioMargins[activeBpDef] : 0;
        return result;
    }

    get aspectRatioStyling(): Partial<CSSStyleDeclaration> {
        if (this.shouldUseFlexBox) return {};

        const paddingPct = (1 / this.realAspectRatio) * 100;
        return ({
            paddingBottom: `calc(${paddingPct}% - ${this.margin}px)`
        });
    }

    get renderContent() {
        return !this.loadingVideo && !this.showCaptionAsContentTitle &&
            (this.content?.caption !== '' || this.content?.header !== '' || this.content?.subheader !== '' || this.content?.callToAction !== null);
    }

    async mounted() {
        this.loadingVideo = !!(this.content?.video || this.content?.vimeoVideoId);
        if (!this.content) return; // For normal blocks (not ad) it will always be not null
        this.rendered = false;
        if (this.content.backgroundImage) {
            this.backgroundImageWidth = this.$el.clientWidth;
        }
        this.initVideo();
        this.$nextTick(() => this.rendered = true);
    }

    initVideo() {
        if (this.content?.video) {
            this.loadingVideo = true;
            this.video = this.$refs.videoMount as any as VideoElm;

            this.video.onloadeddata = () => {
                this.loadingVideo = false;
            };
        } else if (this.content?.vimeoVideoId) {
            this.loadingVideo = true;
            this.vimeoVideoAspectRatio = null;
            this.video = null;
            const options = {
                id: ensureId(this.content.vimeoVideoId),
                autoplay: this.content.isBackgroundVideo,
                loop: this.content.isBackgroundVideo,
                muted: this.content.isBackgroundVideo,
                controls: this.content.showVideoControls,
                title: false,
                byline: false,
                portrait: false,
                color: ((this.isOprema ? tailwind.theme.colors.opremayellow['500'] : tailwind.theme.colors.blue['500']) || '').replace('#', ''),
                autopause: !this.content.isBackgroundVideo // Ensure that all videos on a page will autoplay
            };
            this.$nextTick(() => {
                // eslint-disable-next-line no-new
                this.video = new VimeoPlayer(this.$refs.videoMount as HTMLElement, options) as VimeoPlayer;

                this.video.on('ended', () => {
                    this.isPlaying = false;
                });

                this.video.on('error', (e) => {
                    console.log('vimeo video error', e);
                });
                this.video.on('loaded', () => {
                    this.getVimeoVideoAspectRatio();
                });
            });
        }

        function ensureId(idOrUrl: string): number {
            const idParts = idOrUrl.split(/(\\|\/)/);
            let id = idOrUrl;
            if (idParts.length > 1) {
                id = idParts[idParts.length - 1];
            }
            return parseInt(id, 10);
        }
    }

    @Watch('content', {
        immediate: true
    })
    onContentChange(content: HeroBannerViewModel | undefined) {
        if (!content) return;
        // Set defaults for properties. Width and subheader-font varies depending on whether hero or not
        content.contentAligmentValue = content.contentAligmentValue ?? HorizontalAlignment.Left;
        content.contentWidthValue = content.contentWidthValue ?? (this.isHeroBanner ? ContentWidth.TwoThird : ContentWidth.FullWidth);
        content.subheaderFontSizeValue = content.subheaderFontSizeValue ?? (this.isHeroBanner ? SubheaderFontSize.Large : SubheaderFontSize.Normal);
    }

    async getVimeoVideoAspectRatio() {
        setTimeout(() => {
            this.delayedVideoAspectRatio();
            this.loadingVideo = false;
        }, 200); // 200 seems to be the magic number after the video is loaded.
    }

    async delayedVideoAspectRatio() {
        if (this.video) {
            const width = await this.video.getVideoWidth();
            const height = await this.video.getVideoHeight();
            this.vimeoVideoAspectRatio = { width: width, height: height };
        }
    }

    get isOprema() {
        return serverContextService?.isOprema;
    }

    get headerFontSyncData(): object {
        // Minimum is controlled by current subheader size
        const minSize = this.subheaderSize === 'small' ? 15 : 22;
        return [
            [MIN_LG, { min: minSize, max: 34 }],
            [MIN_MD, { min: minSize, max: 22 }],
            [MIN_XS, { min: minSize, max: 18 }]
        ];
    }

    get hasNoVideoOrVideoHasBeenLoaded(): boolean {
        if (this.content?.vimeoVideoId || this.content?.video) {
            return this.loadingVideo === false && this.vimeoVideoAspectRatio !== null && this.rendered;
        }
        return true;
    }

    toggleVideoState() {
        if (!this.video) return;

        if (this.isPlaying) {
            try {
                this.video.pause();
            } catch (e) {
                loggingService.error(e);
            }
        } else {
            try {
                this.video.play();
            } catch (e) {
                loggingService.error(e);
            }
        }

        this.isPlaying = !this.isPlaying;
    }

    hexToRgbWithDefault(hex: string, alpha?: number): string {
        if (!isValidHexColor(hex)) return tailwind.theme.colors.black;
        return hexToRgb(hex, alpha);
    }

    get backgroundColorStyle(): { backgroundColor: string } {
        let result = 'transparent';

        if (this.content && this.isColorDefined(this.content.backgroundColor)) {
            result = this.hexToRgbWithDefault(this.content.backgroundColor);
        }

        return ({
            backgroundColor: result
        });
    }

    get contentWrapperStyle(): { backgroundColor: string } {
        let result = 'transparent';

        if (this.content && !this.content.backgroundImage && this.isColorDefined(this.content.backgroundColor)) {
            result = this.hexToRgbWithDefault(this.content.backgroundColor);
        }

        return ({
            backgroundColor: result
        });
    }

    get backgroundContentColorStyle(): Partial<CSSStyleDeclaration> {
        if (!this.content || this.loadingVideo) return {};

        const backgroundColor = this.content.contentBackgroundColor;
        const semiTransparent = this.content.makeContentBackgroundTransparent;

        let result = 'transparent';

        if (this.shouldShowContentBackground && this.isColorDefined(backgroundColor)) {
            result = this.hexToRgbWithDefault(backgroundColor, semiTransparent ? semiTransparencyPercentage : undefined);
        }

        this.contentBackgroundColor = result;

        return ({
            backgroundColor: result
        });
    }

    // get backgroundImageFormat() {
    //     return extensionIsGifOrUndefined(this.content?.backgroundImage);
    // }

    get contentTakeoverTransformStyle(): Partial<CSSStyleDeclaration> {
        // Take over banner is 200px taller than normal banners, so we have to push content down by half (100)
        // to ensure it is centered correctly
        const translateY = this.isTakeOverBanner && !this.isMobile ? 100 : 0;

        return ({
            transform: `translateY(${translateY}px)`
        });
    }

    get ctaColor(): string | null {
        return this.content?.callToActionColor || null;
    }

    get headerHeadlineLevel(): string {
        let level;

        if (this.isFirstBlock && !this.content?.caption && this.content?.header) {
            level = 'h1';
        } else if ((this.isFirstBlock && this.content?.caption && this.content?.header) || (!this.isFirstBlock && !this.content?.caption && this.content?.header)) {
            level = 'h2';
        } else {
            level = 'h3';
        }

        return level;
    }

    isColorDefined(color: string): boolean {
        return color?.length === 7 ?? false; // #233445
    }

    colorStyle(hexColor: string): object {
        const EETBlack = tailwind.theme.colors.black;
        return ({
            color: this.contentBackgroundColor === 'white' ? EETBlack : hexColor ? this.hexToRgbWithDefault(hexColor) : EETBlack
        });
    }

    get alignRight(): boolean {
        return this.content?.contentAligmentValue === HorizontalAlignment.Right; // Left is default
    }

    get contentClass(): string {
        if (!this.content) return '';

        let widthCls = 'w-full'; // Default for mobile
        if (!this.isMobile) {
            switch (this.content.contentWidthValue) {
            case ContentWidth.OneThird:
                widthCls = 'w-1/3';
                break;
            case ContentWidth.TwoThird:
                widthCls = 'w-2/3';
                break;
            case ContentWidth.FullWidth:
                widthCls = 'w-full';
                break;
            }
        }
        return `${widthCls}`;
    }

    get subheaderSize(): undefined | 'small' | 'large' {
        if (!this.content) return undefined;

        if (breakpointState.isBreakpointActive(MAX_MD)) {
            return 'small';
        }

        return this.content.subheaderFontSizeValue === SubheaderFontSize.Normal ? 'small' : 'large';
    }

    get adnuntiusImages() : AdImage[] {
        const adImages = (this.content as any as AdContentBlockViewModel).backgroundImages;
        if (!adImages || adImages.length === 0) {
            return [];
        }

        // Take the ones matching aspect-ratio
        return adImages
            .filter(img => isMatchingImageAspectRatio(img, this.realAspectRatio));

        function isMatchingImageAspectRatio(image: AdImage, aspectRatio: number): boolean {
            // Allow for 5 percentage in difference, to be a bit relaxed. Image will overflow correctly
            const allowedDelta = aspectRatio * 0.05;
            return Math.abs(image.width / image.height - aspectRatio) <= allowedDelta;
        }
    }

    get adImageProps(): object | null {
        if (this.adnuntiusImages.length === 0) {
            return null;
        }

        // Src-set
        const srcset = this.adnuntiusImages
            .map(img => `${img.url} ${img.width}w`)
            .join(', ');

        // src. Take largest
        const src = this.adnuntiusImages
            .sort((imageA, imageB) => imageA.width - imageB.width)[this.adnuntiusImages.length - 1].url;

        // Sizes
        const sizes = `${this.realWidthOnScreen}vw`;

        return {
            srcset,
            src,
            sizes
        };
    }

    get shimColor(): Partial<CSSStyleDeclaration> {
        return { backgroundColor: this.content?.shimColor, opacity: '0.3' };
    }

    get videoContainerMoreNarrowThanVideo() {
        return this.realAspectRatio < videoAspectRatio;
    }

    get videoContainerStyle(): Partial<CSSStyleDeclaration> {
        if (!this.content) return {};
        else if (this.content.vimeoVideoId && !this.isHeroBanner) return {};
        else if (this.content.vimeoVideoId && this.isHeroBanner) {
            return this.vimeoVideoAspectRatio ? {
                // @ts-ignore
                aspectRatio: this.vimeoVideoAspectRatio.width / this.vimeoVideoAspectRatio.height
            } : {};
        }

        return this.videoContainerMoreNarrowThanVideo ? ({
            width: 100 + '%',
            height: '100%',
            left: '50%',
            transform: 'translateX(-50%)'
        }) : ({
            ...(!!(this.content.vimeoVideoId) && { height: 100 * videoAspectRatio + '%' }),
            width: '100%',
            top: '50%',
            transform: 'translateY(-50%)'
        });
    }

    get shouldShowContentBackground(): boolean {
        return this.showBackgroundImage || !!((this.content as any as AdContentBlockViewModel)?.backgroundImages?.length > 0) || !!this.content?.vimeoVideoId;
    }

    get showHtmlCustomControls() {
        return this.content && this.loadingVideo === false && this.video && this.content.isBackgroundVideo === false && this.content.showVideoControls === false;
    }

    get showCaptionAsContentTitle() {
        return this.content?.captionLocation === 'Top';
    }

    get showBackgroundImage(): boolean {
        return !!(this.content?.backgroundImage?.url ?? false);
    }

    get showShim(): boolean {
        return !!this.content?.shimColor && (!!this.content.vimeoVideoId || !!this.content.video || this.showBackgroundImage || !!this.adImageProps);
    }

    get showCallToActionButton(): boolean {
        // Show button if there are no overlayVimeoVideoId (then another button should be shown)
        return !!(this.content?.callToAction?.name ?? false);
    }

    get showCallToActionBlock(): boolean {
        return !this.showCallToActionButton && (!!this.content?.callToAction?.url ?? false);
    }

    get showCallToActionVideoOverlayButton(): boolean {
        return !!(this.content?.overlayVimeoVideoId && this.content?.overlayVimeoCta);
    }

    get isTakeOverBanner() {
        return this.isFirstBlock && themeService.isTakeOverEnabled;
    }

    async clickOpenVideoOverlay() {
        if (this.content?.overlayVimeoVideoId) {
            if (this.video) {
                // Pauses video background while playing overlay video
                this.video.pause();
            }
            await videoFullscreenOverlayService.show(this.content.overlayVimeoVideoId, true);
            if (this.video) {
                // Starts video background again, after video overlay has been closed
                this.video.play();
            }
        }
    }
}
